import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  Paper,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { IconDataTable } from '@/common/components/IconDataTable.tsx';
import {
  ArrowLeftIcon,
  ChatIcon,
  CopyIcon,
  EyeClosedIcon,
  EyeIcon,
  ListChecksIcon,
  NoteIcon,
} from '@phosphor-icons/react';
import useWorkOrder from '@/features/work-orders/hooks/useWorkOrder.tsx';
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  GetWorkOrderResponseDto,
  UpdateWorkOrderWatchDtoEventKeysItem,
} from '@/generated/api/pacApi.schemas.ts';
import { MessagesList } from '@/common/components/MessagesList.tsx';
import {
  type WorkOrderTask,
  WorkOrderTaskList,
} from '@/features/work-orders/components/WorkOrderTaskList.tsx';
import { useEffect, useMemo, useState } from 'react';
import WorkOrderStatusBadge from '@/features/work-orders/components/WorkOrderStatusBadge.tsx';
import Markdown from 'react-markdown';
import { ErrorBoundary } from 'react-error-boundary';
import WorkOrderLiveHours from '@/features/work-orders/components/WorkOrderLiveHours.tsx';
import { useClipboard } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  getWorkOrderControllerGetWorkOrderWatchStateQueryKey,
  useWorkOrderControllerDeleteWorkOrderWatchState,
  useWorkOrderControllerGetWorkOrderWatchState,
  useWorkOrderControllerUpdateWorkOrderWatchState,
} from '@/generated/api/work-order.ts';
import SupportCasesTab from '@/features/work-orders/components/detail-tabs/SupportCasesTab.tsx';
import SupportCasesTabBody from '@/features/work-orders/components/detail-tabs/SupportCasesTabBody.tsx';
import useSidebarStack from '@/common/sidebar/useSidebarStack.ts';
import HrefButton from '@/common/components/HrefButton.tsx';
import WorkOrderDetailsFinanceSection from '@/features/work-orders/components/detail-tabs/WorkOrderDetailsFinanceSection.tsx';
import type { ExtraSidebarParams } from '@/common/sidebar/sidebar-registry.tsx';
import CollapsedSidebar from '@/common/sidebar/CollapsedSidebar.tsx';
import DateTimeCell from '@/common/components/data-table/DateTimeCell.tsx';
import WorkOrderRelatedItems from './WorkOrderRelatedItems.tsx';
import WorkOrderTitle from '@/features/work-orders/components/detail-tabs/WorkOrderTitle.tsx';
import WorkOrderLocation from '@/features/work-orders/components/detail-tabs/WorkOrderLocation.tsx';
import WorkOrderAlerts from '@/features/work-orders/components/WorkOrderAlerts.tsx';
import { WORK_ORDER_AUTO_REFRESH_QUERY_OPTIONS } from '../autoRefresh.ts';

function mutedEmpty(label = 'Not provided') {
  return <Text c="dimmed">{label}</Text>;
}

function toTaskStatus(status?: string | null): WorkOrderTask['status'] {
  return status?.toLowerCase() === 'complete' ? 'complete' : 'incomplete';
}

type WorkOrderDetailsDrawerProps = {
  workOrderId: string;
} & ExtraSidebarParams;

export default function WorkOrderDetailsSidebar({
  workOrderId,
  depth,
  hovered,
  collapsed,
}: WorkOrderDetailsDrawerProps) {
  const sidebarStack = useSidebarStack();

  const clipboard = useClipboard();
  const queryClient = useQueryClient();
  const [watchModalOpen, setWatchModalOpen] = useState(false);
  const [watchEventKeys, setWatchEventKeys] = useState<
    UpdateWorkOrderWatchDtoEventKeysItem[]
  >([]);

  const workOrder = useWorkOrder(workOrderId) as UseQueryResult<
    GetWorkOrderResponseDto,
    Error
  >;
  const watchQuery = useWorkOrderControllerGetWorkOrderWatchState(workOrderId, {
    query: {
      ...WORK_ORDER_AUTO_REFRESH_QUERY_OPTIONS,
      enabled: Boolean(workOrderId),
    },
  });
  const watchQueryKey = workOrderId
    ? getWorkOrderControllerGetWorkOrderWatchStateQueryKey(workOrderId)
    : null;
  const updateWatchMutation = useWorkOrderControllerUpdateWorkOrderWatchState({
    mutation: {
      onSuccess: (data) => {
        if (watchQueryKey) queryClient.setQueryData(watchQueryKey, data);
        setWatchModalOpen(false);
        notifications.show({
          message: data.watched
            ? 'Work order watch settings saved'
            : 'Stopped watching work order',
          color: 'green',
        });
      },
      onError: () => {
        notifications.show({
          message: 'Failed to save watch settings',
          color: 'red',
        });
      },
    },
  });
  const deleteWatchMutation = useWorkOrderControllerDeleteWorkOrderWatchState({
    mutation: {
      onSuccess: (data) => {
        if (watchQueryKey) queryClient.setQueryData(watchQueryKey, data);
        setWatchModalOpen(false);
        notifications.show({
          message: 'Stopped watching work order',
          color: 'green',
        });
      },
      onError: () => {
        notifications.show({
          message: 'Failed to stop watching work order',
          color: 'red',
        });
      },
    },
  });

  const messages = useMemo(
    () =>
      workOrder.data
        ? workOrder.data.messages.map((message) => ({
            id: message.id,
            message: message.message,
            createdAt: message.timestamp,
            fromUser: message.authorName,
          }))
        : [],
    [workOrder.data],
  );

  const tasks = useMemo(
    () =>
      workOrder.data
        ? workOrder.data.tasks.map((task) => ({
            id: task.id,
            description: task.description,
            typeName: task.type,
            status: toTaskStatus(task.status),
          }))
        : [],
    [workOrder.data],
  );
  const remainingTaskCount = useMemo(
    () => tasks.filter((task) => task.status === 'incomplete').length,
    [tasks],
  );
  const customFields = useMemo(
    () =>
      workOrder.data
        ? [...workOrder.data.customFields].sort((a, b) =>
            a.name.localeCompare(b.name),
          )
        : [],
    [workOrder.data],
  );
  const latestMessageAt = useMemo(() => {
    const timestamps: { s: string; d: Date }[] = [];

    for (const message of messages) {
      if (!message.createdAt) continue;

      const date = new Date(message.createdAt);
      if (!Number.isNaN(date.getTime()))
        timestamps.push({ s: message.createdAt, d: date });
    }

    timestamps.sort((a, b) => b.d.getTime() - a.d.getTime());

    return timestamps[0]?.s;
  }, [messages]);

  const latestCheckIn = workOrder.data?.timeLogs[0]?.inAt ?? null;
  const latestCheckOut = workOrder.data?.timeLogs[0]?.outAt ?? null;
  const checkedIn = latestCheckIn !== null && latestCheckOut == null;
  const totalHoursOnSite = useMemo(
    () =>
      workOrder.data?.timeLogs
        ?.map((timeLog) => timeLog.hours)
        .reduce((totalHours, hours) => totalHours + hours, 0) ?? 0,
    [workOrder.data],
  );

  const serviceDate = workOrder.data?.serviceDate;
  const supportedWatchEvents = watchQuery.data?.supportedEvents ?? [];
  const watchBusy =
    updateWatchMutation.isPending || deleteWatchMutation.isPending;
  const watchDisabled =
    !workOrderId ||
    watchQuery.isLoading ||
    (watchQuery.isError && !watchQuery.data);

  useEffect(() => {
    if (!watchModalOpen) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setWatchEventKeys(
      (watchQuery.data?.watched
        ? watchQuery.data.eventKeys
        : (watchQuery.data?.supportedEvents.map((event) => event.key) ??
          [])) as UpdateWorkOrderWatchDtoEventKeysItem[],
    );
  }, [watchModalOpen, watchQuery.data]);

  function openWatchModal() {
    if (watchDisabled) return;
    setWatchModalOpen(true);
  }

  function saveWatchSettings() {
    if (!workOrderId) return;

    updateWatchMutation.mutate({
      workOrderId: workOrderId,
      data: { eventKeys: watchEventKeys },
    });
  }

  function stopWatching() {
    if (!workOrderId) return;
    deleteWatchMutation.mutate({ workOrderId: workOrderId });
  }

  if (collapsed) {
    return (
      <CollapsedSidebar
        title={`Work Order #${workOrder.data?.externalId ?? 'Unknown'}`}
        depth={depth}
        hovered={hovered}
      />
    );
  }

  return (
    <>
      <Modal
        opened={watchModalOpen}
        onClose={() => setWatchModalOpen(false)}
        title="Watch work order"
        size="md"
      >
        <Stack gap="md">
          <Checkbox.Group
            value={watchEventKeys}
            onChange={(value) =>
              setWatchEventKeys(value as UpdateWorkOrderWatchDtoEventKeysItem[])
            }
          >
            <Stack gap="sm">
              {Object.entries(groupWatchEvents(supportedWatchEvents)).map(
                ([group, events]) => (
                  <Stack key={group} gap={6}>
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                      {group}
                    </Text>
                    <Stack gap={4}>
                      {events.map((event) => (
                        <Checkbox
                          key={event.key}
                          value={event.key}
                          label={event.label}
                          disabled={watchBusy}
                        />
                      ))}
                    </Stack>
                  </Stack>
                ),
              )}
            </Stack>
          </Checkbox.Group>

          <Group justify="space-between">
            <Button
              variant="subtle"
              color="red"
              disabled={!watchQuery.data?.watched || watchBusy}
              loading={deleteWatchMutation.isPending}
              onClick={stopWatching}
            >
              Stop watching
            </Button>
            <Group gap="xs">
              <Button
                variant="default"
                disabled={watchBusy}
                onClick={() => setWatchModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                disabled={watchBusy}
                loading={updateWatchMutation.isPending}
                onClick={saveWatchSettings}
              >
                Save
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
      <Stack
        gap="md"
        h="100%"
        style={{
          overflow: 'hidden',
        }}
      >
        <Group>
          <ActionIcon
            size="sm"
            variant="transparent"
            onClick={() => sidebarStack.popItem()}
          >
            <ArrowLeftIcon size={32} />
          </ActionIcon>

          <Group justify="space-between" style={{ flex: 1 }}>
            <Title order={4}>Work Order</Title>

            <Group>
              <ActionIcon
                variant={watchQuery.data?.watched ? 'light' : 'subtle'}
                color={watchQuery.data?.watched ? 'blue' : 'gray'}
                disabled={watchDisabled}
                loading={watchQuery.isLoading}
                aria-label={
                  watchQuery.data?.watched
                    ? 'Edit work order watch settings'
                    : 'Watch work order'
                }
                onClick={openWatchModal}
              >
                {watchQuery.data?.watched ? <EyeIcon /> : <EyeClosedIcon />}
              </ActionIcon>
            </Group>
          </Group>
        </Group>

        <WorkOrderAlerts workOrderId={workOrder.data?.id} />

        {workOrder.isLoading ? (
          <ScrollArea
            style={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <Stack gap="sm">
              <Skeleton height={32} radius="md" />
              <Skeleton height={118} radius="md" />
              <Skeleton height={176} radius="md" />
            </Stack>
          </ScrollArea>
        ) : workOrder.isError && !workOrder.data ? (
          <ScrollArea
            style={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <Alert color="red" title="Work order failed to load">
              <Stack gap="sm">
                <Text size="sm">
                  Check your connection and retry. If this continues, return to
                  the work order list and select the order again.
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => void workOrder.refetch()}
                >
                  Retry
                </Button>
              </Stack>
            </Alert>
          </ScrollArea>
        ) : workOrder.data ? (
          <Tabs
            defaultValue="overview"
            styles={{
              tab: {
                minHeight: 32,
                paddingBlock: 6,
                paddingInline: 8,
                fontSize: 13,
              },
              tabSection: {
                '&[data-position="left"]:not(:only-child)': {
                  marginInlineEnd: 5,
                },
                '&[data-position="right"]:not(:only-child)': {
                  marginInlineStart: 5,
                },
              },
            }}
            style={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
              flexWrap: 'nowrap',
            }}
          >
            <Tabs.List
              mb="sm"
              style={{
                flexShrink: 0,
                flexWrap: 'nowrap',
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
            >
              <Tabs.Tab value="overview" leftSection={<NoteIcon size={14} />}>
                Overview
              </Tabs.Tab>
              <Tabs.Tab
                value="messages"
                leftSection={<ChatIcon size={14} />}
                rightSection={
                  <Badge size="xs" color={messages.length ? 'blue' : 'gray'}>
                    {messages.length}
                  </Badge>
                }
              >
                Messages
              </Tabs.Tab>
              <SupportCasesTab workOrderId={workOrder.data.id} />
              <Tabs.Tab
                value="tasks"
                leftSection={<ListChecksIcon size={14} />}
                rightSection={
                  <Badge size="xs" color={remainingTaskCount ? 'red' : 'gray'}>
                    {remainingTaskCount}
                  </Badge>
                }
              >
                Tasks
              </Tabs.Tab>
              <Tabs.Tab
                value="custom-fields"
                leftSection={<NoteIcon size={14} />}
              >
                Fields
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel
              value="overview"
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <ScrollArea style={{ height: '100%' }}>
                <Stack gap="sm">
                  <Title order={5}>Details</Title>
                  <Paper withBorder radius="md" p="sm">
                    <Stack gap="sm">
                      <SimpleGrid cols={2} spacing="sm">
                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Work Order #
                          </Text>
                          <Group gap={8} align="center">
                            <HrefButton
                              href={`https://app.fieldnation.com/workorders/${workOrder.data.externalId}`}
                            >
                              {workOrder.data.externalId ?? 'Unknown'}
                            </HrefButton>
                            <ActionIcon
                              variant="transparent"
                              size="xs"
                              onClick={() => {
                                clipboard.copy(workOrder.data.externalId);
                                notifications.show({
                                  message: 'Work Order ID copied to clipboard',
                                  color: 'green',
                                });
                              }}
                            >
                              <CopyIcon />
                            </ActionIcon>
                          </Group>
                        </Stack>
                      </SimpleGrid>

                      <WorkOrderTitle title={workOrder.data.title} />

                      <SimpleGrid cols={2} spacing="sm">
                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Client
                          </Text>
                          <Text size="sm" fw={600}>
                            {workOrder.data.clientName ?? '-'}
                          </Text>
                        </Stack>

                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Project
                          </Text>
                          <Text size="sm" fw={600}>
                            {workOrder.data.projectName ?? '-'}
                          </Text>
                        </Stack>

                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Assignee
                          </Text>
                          <Text size="sm" fw={600}>
                            {workOrder.data.technicianName ?? 'Unassigned'}
                          </Text>
                        </Stack>

                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Status
                          </Text>
                          <WorkOrderStatusBadge
                            status={workOrder.data.statusDisplay}
                          />
                        </Stack>

                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Remaining tasks
                          </Text>
                          <Text size="sm" fw={600}>
                            {remainingTaskCount} of {tasks.length}
                          </Text>
                        </Stack>
                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Latest message
                          </Text>
                          {latestMessageAt ? (
                            <Text size="sm" fw={600}>
                              <DateTimeCell
                                value={latestMessageAt}
                                valueRepresents="utcInstant"
                                mode="dateTime"
                                sourceTimeZone={workOrder.data.timezone}
                                timeZoneStyle="inline"
                                emptyLabel="-"
                              />
                            </Text>
                          ) : (
                            <Text size="sm" fw={600} c="dimmed">
                              -
                            </Text>
                          )}
                        </Stack>
                      </SimpleGrid>
                    </Stack>
                  </Paper>

                  <Title order={5}>Schedule / Time</Title>
                  <Paper withBorder radius="md" p="sm">
                    <Stack gap="sm">
                      <Stack gap={2}>
                        <Text size="xs" c="dimmed">
                          Scheduled
                        </Text>
                        <Text size="sm" fw={600}>
                          <DateTimeCell
                            value={serviceDate ?? null}
                            valueRepresents="utcInstant"
                            mode="dateTime"
                            sourceTimeZone={workOrder.data.timezone}
                            timeZoneStyle="inline"
                            emptyLabel="Not scheduled"
                          />
                        </Text>
                      </Stack>

                      <SimpleGrid cols={2} spacing="sm">
                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Latest Check In
                          </Text>
                          {latestCheckIn ? (
                            <Text size="sm" fw={600}>
                              <DateTimeCell
                                value={latestCheckIn}
                                valueRepresents="utcInstant"
                                mode="dateTime"
                                sourceTimeZone={workOrder.data.timezone}
                                timeZoneStyle="inline"
                                emptyLabel="-"
                              />
                            </Text>
                          ) : (
                            <Text size="sm" fw={600} c="dimmed">
                              -
                            </Text>
                          )}
                        </Stack>
                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Latest Check Out
                          </Text>
                          {latestCheckOut ? (
                            <Text size="sm" fw={600}>
                              <DateTimeCell
                                value={latestCheckOut}
                                valueRepresents="utcInstant"
                                mode="dateTime"
                                sourceTimeZone={workOrder.data.timezone}
                                timeZoneStyle="inline"
                                emptyLabel="-"
                              />
                            </Text>
                          ) : (
                            <Text size="sm" fw={600} c="dimmed">
                              -
                            </Text>
                          )}
                        </Stack>
                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Latest Hours On-Site
                          </Text>
                          {checkedIn ? (
                            <WorkOrderLiveHours
                              key={workOrder.data.id}
                              inAt={latestCheckIn!}
                              estimate={workOrder.data.estimatedRequiredHours}
                            />
                          ) : (
                            <Text size="sm" fw={600} c="dimmed">
                              -
                            </Text>
                          )}
                        </Stack>
                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Total Hours On-Site
                          </Text>
                          <Text size="sm" fw={600}>
                            {checkedIn ? (
                              <WorkOrderLiveHours
                                key={workOrder.data.id}
                                inAt={latestCheckIn!}
                                plus={totalHoursOnSite}
                                estimate={workOrder.data.estimatedRequiredHours}
                              />
                            ) : (
                              <Text size="sm" fw={600}>
                                {totalHoursOnSite.toFixed(2)}
                              </Text>
                            )}
                          </Text>
                        </Stack>
                      </SimpleGrid>
                    </Stack>
                  </Paper>

                  <Title order={5}>Location</Title>
                  <Paper withBorder radius="md" p="sm">
                    <WorkOrderLocation
                      locationId={workOrder.data.locationId}
                      locationName={workOrder.data.locationName}
                      address1={workOrder.data.address1}
                      address2={workOrder.data.address2}
                      city={workOrder.data.locationCity}
                      state={workOrder.data.locationState}
                      zip={workOrder.data.locationZip}
                      country={workOrder.data.locationCountry}
                    />
                  </Paper>

                  <Title order={5}>Related items</Title>
                  <WorkOrderRelatedItems
                    workOrderId={workOrder.data.id}
                    scheduledAt={workOrder.data.serviceDate}
                    timeZone={workOrder.data.timezone}
                  />

                  <Title order={5}>Finance</Title>
                  <Paper withBorder radius="md" p="sm">
                    <WorkOrderDetailsFinanceSection
                      workOrder={workOrder.data!}
                    />
                  </Paper>

                  <Title order={5}>Service Description</Title>
                  <Paper withBorder radius="md" px="md" py="sm">
                    <ErrorBoundary
                      fallback={
                        <Text c="red">
                          Something went wrong when loading the service
                          description
                        </Text>
                      }
                    >
                      <Markdown>{workOrder.data.serviceDescription}</Markdown>
                    </ErrorBoundary>
                  </Paper>
                </Stack>
              </ScrollArea>
            </Tabs.Panel>
            <Tabs.Panel
              value="messages"
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <ScrollArea style={{ height: '100%' }}>
                <Stack gap="sm">
                  {messages.length ? (
                    <MessagesList messages={messages} />
                  ) : (
                    <Alert color="gray" title="No messages yet">
                      This work order does not have any messages.
                    </Alert>
                  )}
                </Stack>
              </ScrollArea>
            </Tabs.Panel>
            <SupportCasesTabBody workOrderId={workOrder.data.id} />
            <Tabs.Panel
              value="tasks"
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <ScrollArea style={{ height: '100%' }}>
                {tasks.length ? (
                  <WorkOrderTaskList tasks={tasks}></WorkOrderTaskList>
                ) : (
                  <Alert color="gray" title="No tasks yet">
                    This work order does not have any tasks.
                  </Alert>
                )}
              </ScrollArea>
            </Tabs.Panel>
            <Tabs.Panel
              value="custom-fields"
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <ScrollArea style={{ height: '100%' }}>
                <Stack gap="sm">
                  {customFields.length ? (
                    <IconDataTable
                      rows={customFields.map((field) => ({
                        icon: NoteIcon,
                        label: field.name,
                        value: field.value || mutedEmpty(),
                      }))}
                    />
                  ) : (
                    <Alert color="gray" title="No custom fields">
                      This work order does not have any custom fields.
                    </Alert>
                  )}
                </Stack>
              </ScrollArea>
            </Tabs.Panel>
          </Tabs>
        ) : (
          <ScrollArea
            style={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <Alert color="yellow" title="Work order not found">
              Select a work order from the list to view its details.
            </Alert>
          </ScrollArea>
        )}
      </Stack>
    </>
  );
}

function groupWatchEvents(
  events: { key: string; label: string; group: string }[],
) {
  return events.reduce<Record<string, typeof events>>((groups, event) => {
    groups[event.group] = [...(groups[event.group] ?? []), event];
    return groups;
  }, {});
}
